import { useQuery } from '@apollo/client';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BookAppointmentInfoContainer,
  BookAppointmentInfoTitleContainer
} from './BookAppointmentWidget.styled';
import { CalendarPlus } from 'assets/icons/Icons';
import { Button } from 'components/v2/Buttons/Button/Button';
import { Heading } from 'components/v2/Typography/Heading';
import { Text } from 'components/v2/Typography/Text';
import { usePatient } from 'hooks/usePatient/usePatient';
import { themes } from 'kb-shared';
import { QUERY_MEMBERSHIP } from 'kb-shared/graphql/queries';
import { QueryMembershipResponse } from 'kb-shared/graphql/queries_types';
import { CustomList } from 'screens/Dashboard/CustomWidget/Components/CustomList';
import {
  ButtonsContainer,
  IconContainer,
  IllustratedBackgroundWidget,
  WidgetTitleContainer
} from 'screens/Dashboard/Widgets/Widgets.styled';
import { analytics } from 'utilities/analytics';
import { pageUrl, RoutesPath } from 'utilities/pageUrl';

import {
  componentDescription,
  componentTitle,
  EXCLUDED_PARTNER_CLINIC_EMPLOYERS,
  getAppointmentsOfferString
} from './BookAppointmentWidget.utils';

export const BookAppointmentWidget: React.FC = () => {
  const navigate = useNavigate();
  const { patient } = usePatient();

  const { data: membershipData } = useQuery<QueryMembershipResponse>(QUERY_MEMBERSHIP, {
    variables: { membershipId: patient?.patientMembership?.membershipId },
    skip: !patient?.patientMembership?.membershipId
  });

  const employerName = patient?.patientMembership?.employerName;
  const isExcludedEmployer =
    employerName && EXCLUDED_PARTNER_CLINIC_EMPLOYERS.includes(employerName);

  const showPartnerClinicButton =
    patient?.patientMembership &&
    patient?.patientMembership?.membershipId &&
    patient?.patientMembership?.employer &&
    !membershipData?.membership?.hidePartnerClinics &&
    !isExcludedEmployer;

  return (
    <IllustratedBackgroundWidget variant={9}>
      <IconContainer $iconWidth={42} $iconHeight={48}>
        <CalendarPlus type="solid" />
      </IconContainer>
      <WidgetTitleContainer>
        <Heading tag="div" styledAs="h2">
          {componentTitle}
        </Heading>
      </WidgetTitleContainer>
      <BookAppointmentInfoTitleContainer>
        <Text color={themes.colors.neutral.primaryNavy} fontStyle="medium" size="lg">
          {componentDescription}
        </Text>
      </BookAppointmentInfoTitleContainer>
      <BookAppointmentInfoContainer>
        <CustomList items={getAppointmentsOfferString()} type="bulleted" />
      </BookAppointmentInfoContainer>

      <ButtonsContainer>
        {showPartnerClinicButton ? (
          <>
            <Button
              category="primary-dark"
              size="lg"
              fullWidth
              label="Book Your Appointment at Kindbody"
              onClick={() => {
                analytics.track(analytics.EVENTS.APPOINTMENT_BOOKING_STARTED);
                navigate(pageUrl.booking.bookingQuiz());
              }}
            />
            <Button
              category="secondary"
              size="lg"
              fullWidth
              label="Find a Partner Clinic (for Patients with Kindbody Employer Benefits Only)"
              onClick={() =>
                navigate(`${RoutesPath.BOOKING_BOOK}?step=location&partner_clinic_search=search`)
              }
            />
          </>
        ) : (
          <Button
            category="primary-dark"
            size="lg"
            fullWidth
            label="Book an Appointment"
            onClick={() => {
              analytics.track(analytics.EVENTS.APPOINTMENT_BOOKING_STARTED);
              navigate(pageUrl.booking.bookingQuiz());
            }}
          />
        )}
      </ButtonsContainer>
    </IllustratedBackgroundWidget>
  );
};
